import { CertificationPrintDialogComponent } from '@/components/certification-print-dialog/certification-print-dialog.component';
import { ConfirmDialogComponent } from '@/components/confirm-dialog/confirm-dialog.component';
import { PropertyCertifyDialogComponent } from '@/components/property-certify-dialog/property-certify-dialog.component';
import { PropertyOwnersDialogComponent } from '@/components/property-owners-dialog/property-owners-dialog.component';
import {
  PropertySettingsDialogComponent,
  PropertySettingsDialogData
} from '@/components/property-settings-dialog/property-settings-dialog.component';
import { PropertyTransfertDialogComponent } from '@/components/property-transfert-dialog/property-transfert-dialog.component';
import { DateTimeFormatPipe } from '@/extensions/dateTimePipe';
import { CertificationHelper } from '@/helpers/certification.helper';
import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { Paging } from '@/models/Paging';
import { AlertService } from '@/services/alert.service';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterLink } from '@angular/router';
import {
  AdminService,
  CertificationsService,
  PropertiesService,
  PropertyChildrenTypeFilter,
  PropertyType,
  RealProperty,
  SortDirection
} from '@eemo/api-client';
import { Subscription, catchError, map, merge, of, startWith, switchMap } from 'rxjs';
import { DateFormatPipe } from '../../../extensions/datePipe';

import { BooleanFormatPipe } from '@/extensions/booleanPipe';
import { ActionService } from '@/services/action.service';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-admin-properties',
  templateUrl: './admin-properties.component.html',
  styleUrls: ['./admin-properties.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    NgFor,
    MatSlideToggleModule,
    MatDividerModule,
    NgIf,
    MatProgressSpinnerModule,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    RouterLink,
    MatMenuModule,
    NgClass,
    MatPaginatorModule,
    DateFormatPipe,
    DateTimeFormatPipe,
    BooleanFormatPipe
  ]
})
export class AdminPropertiesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  isLoadingResults = true;
  dataSource: MatTableDataSource<RealProperty>;
  displayFullColumns = false;
  lightColumns = ['name', 'type', 'ownerUserName', 'city', 'eemoId', 'certificationType', 'actions'];
  fullColumns = [
    'name',
    'type',
    'ownerUserName',
    'coOwnerUserName',
    'city',
    'eemoId',
    'certificationType',
    'certificationDate',
    'archived',
    'dateCreate',
    'dateUpdate',
    'uid',
    'actions'
  ];
  displayedColumns = [];

  search: string;
  filterType: PropertyType;
  availableTypes: PropertyType[];
  typesDisplay: { [key: string]: string } = {};

  sub = new Subscription();
  sortSub = new Subscription();
  deleteSub = new Subscription();

  paging = new Paging();

  constructor(
    private propertiesService: PropertiesService,
    private adminService: AdminService,
    private certifService: CertificationsService,
    private dialog: MatDialog,
    private alertService: AlertService,
    private changeDetectorRefs: ChangeDetectorRef,
    public propHelper: PropertyHelper,
    public certificationHelper: CertificationHelper,
    public userHelper: UserHelper,
    public actionService: ActionService
  ) {
    this.displayedColumns = this.lightColumns;
  }

  ngOnInit() {
    this.availableTypes = Object.keys(PropertyType) as PropertyType[];
    this.availableTypes.forEach((t) => (this.typesDisplay[t] = this.propHelper.getTypeName(t)));
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.sortSub.unsubscribe();
    this.deleteSub.unsubscribe();
  }

  ngAfterViewInit() {
    // If the user changes the sort order, reset back to the first page.
    this.sortSub = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.loadData();
  }

  loadData() {
    this.sub = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;
          const sortDirection = this.sort.direction
            ? this.sort.direction == 'desc'
              ? SortDirection.DESC
              : SortDirection.ASC
            : null;
          const sortField = this.sort.active as 'name' | 'eemoId' | 'certificationDate' | 'dateCreate' | 'dateUpdate';
          return this.propertiesService.getRealPropertyList(
            null,
            null,
            this.filterType ? [this.filterType] : null,
            this.search || null,
            null,
            null,
            null,
            PropertyChildrenTypeFilter.ALL,
            sortField,
            sortDirection,
            this.paginator.pageIndex * this.paginator.pageSize,
            this.paginator.pageSize
          );
        }),
        map((dataPage) => {
          // Flip flag to show that loading has finished.
          this.isLoadingResults = false;
          this.paging.length = dataPage.paging.totalItems;
          return dataPage.list;
        }),
        catchError((error) => {
          this.isLoadingResults = false;
          return of([]);
        })
      )
      .subscribe((data) => {
        const displayedData = [];
        data.forEach((i) => {
          const row = {
            prop: i,
            name: i.name,
            eemoId: i.eemoId,
            demo: i.demo,
            uid: i.uid,
            type: this.propHelper.getTypeName(i.type),
            owner: UserHelper.userSummmaryDisplayName(i.owner),
            coOwner: i.coOwner ? UserHelper.userSummmaryDisplayName(i.coOwner) : '',
            certificationType: this.certificationHelper.getCertificationTypeName(i.certificationType)
          };
          displayedData.push(row);
        });
        this.dataSource = new MatTableDataSource(displayedData);
      });
  }

  applyFilter(filterValue: string) {
    if (!filterValue || filterValue.length > 1) {
      this.search = filterValue.trim().toLowerCase();
      this.paginator.pageIndex = 0;
      this.loadData();
    }
  }

  updateDataFromFilters() {
    this.paginator.pageIndex = 0;
    this.loadData();
  }

  toggleDisplayColumns() {
    if (this.displayFullColumns) {
      this.displayFullColumns = false;
      this.displayedColumns = this.lightColumns;
    } else {
      this.displayFullColumns = true;
      this.displayedColumns = this.fullColumns;
    }
    this.changeDetectorRefs.detectChanges();
  }

  confirmDelete(prop: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: 'Confirmez-vous la suppression du bien "' + prop.name + '" ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDelete(prop);
        }
      });
    }
  }

  doDelete(prop: RealProperty) {
    this.deleteSub = this.propertiesService.deleteRealProperty(prop.uid).subscribe((result) => {
      this.alertService.success('Suppression effectuée avec succès');
      this.loadData();
    });
  }

  certify(prop: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyCertifyDialogComponent, {
        data: { prop }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  transfertOwnership(prop: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyTransfertDialogComponent, {
        // width: '20rem'
        data: { prop }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  manageCoOwner(prop: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyOwnersDialogComponent, {
        // width: '20rem'
        data: { prop }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  printCertification(prop: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(CertificationPrintDialogComponent, {
        // width: '20rem'
        data: { prop }
      });
    }
  }

  settings(itemToEdit: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const data: PropertySettingsDialogData = { property: itemToEdit };
      const dialogRef = this.dialog.open(PropertySettingsDialogComponent, {
        data
      });

      dialogRef.afterClosed().subscribe((result) => {
        this.loadData();
      });
    }
  }
}
